import { BenefitTypeOptions } from '@strata/constants';
import { sortAsc } from '@strata/helper-functions';
import { FormContainer } from '@strata/react-form-container';
import { InlineMessage } from '@strata/react-inline-message';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { FormPage } from 'components/FormPage';
import { ListPageHeader } from 'components/ListPageHeader';
import { RoutePreviewSection } from 'pages/routeManagement/RoutePreviewSection';
import { RouteRuleForm } from 'pages/routeManagement/RouteRuleForm';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { mapPayerSegment } from 'services/PayerSegmentSupport';
import {
    getSegmentPlacement,
    globalRoutesAppRoute,
    globalRouteSegmentsUrl,
    globalRoutesUrl,
    globalRouteUrl,
    useConditionOptions,
    useConfiguredSegmentRoutes,
} from 'services/RouteRuleHelpers';

/** The form itself, inside Formik so the conditions it offers can follow the selected benefit */
const GlobalRouteFormContent = ({
    isCreate,
    isLoading,
    payerSegmentOptions = undefined,
    configuredSegmentRoutes = undefined,
    onCancel,
}) => {
    const formik = useFormikContext();
    const { benefit } = formik.values;

    const { conditionOptionsMap, networkLists, supplierOptions } = useConditionOptions(
        undefined,
        benefit,
        true,
    );

    return (
        <FormPage
            columns={[
                <FormContainer
                    key="form"
                    formTitle={`${isCreate ? 'Create' : 'Edit'} Global Route`}
                    isLoading={isLoading}
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={onCancel}
                    formLeftContent={
                        <>
                            <div className="margin__bottom--medium">
                                <InlineMessage
                                    intent={InlineMessage.Intent.INFO}
                                    title="GLOBAL ROUTE"
                                    wrapText
                                    label="Changes made here will affect routing for all segments using this route rule."
                                />
                            </div>
                            <ListPageHeader pageTitle="Route Rule" />
                            <SuggestSingleSelectFormik
                                id="benefit"
                                label="Benefit"
                                required
                                // The rule sits in each segment's routes for one benefit only
                                disabled={!isCreate}
                                values={BenefitTypeOptions}
                                onChange={(newValue) =>
                                    formik.setValues({
                                        ...formik.values,
                                        benefit: newValue?.value,
                                        // Placement points at routes of the benefit it was picked from
                                        payer_segments: formik.values.payer_segments.map(
                                            (segment) => ({
                                                ...segment,
                                                sequence: undefined,
                                                rule_placement: undefined,
                                                before_continuity: undefined,
                                                route_sequence: undefined,
                                            }),
                                        ),
                                    })
                                }
                                selectedItem={BenefitTypeOptions.find(
                                    ({ value }) => value === benefit,
                                )}
                            />
                            <RouteRuleForm
                                benefit={benefit}
                                conditionOptionsMap={conditionOptionsMap}
                                payerSegmentOptions={payerSegmentOptions}
                                configuredSegmentRoutes={configuredSegmentRoutes}
                            />
                        </>
                    }
                />,
                <RoutePreviewSection
                    key="preview"
                    benefit={benefit}
                    isLoading={isLoading}
                    payerSegmentOptions={payerSegmentOptions}
                    conditionOptionsMap={conditionOptionsMap}
                    supplierOptions={supplierOptions}
                    networkOptions={networkLists}
                />,
            ]}
        />
    );
};

GlobalRouteFormContent.propTypes = {
    isCreate: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    payerSegmentOptions: PropTypes.arrayOf(
        PropTypes.shape({ name: PropTypes.string, value: PropTypes.string }),
    ),
    configuredSegmentRoutes: PropTypes.objectOf(
        PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string })),
    ),
    onCancel: PropTypes.func.isRequired,
};

/** Create or edit a route rule that is configured in payer segments across payers */
export const GlobalRouteDetails = () => {
    const { globalRouteId } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate(globalRoutesAppRoute);

    const isCreate = globalRouteId === CREATE_ROUTE;

    const globalRoute = useGet(isCreate ? null : globalRouteUrl(globalRouteId));

    // Which payer segments have the rule is only known to the payer databases holding it
    const payerSegmentsWithRoute = useGet(
        globalRoute ? globalRouteSegmentsUrl(globalRoute.benefit, globalRouteId) : null,
    );

    const payerSegments = useGet('/reference/payer_segments');
    const payerSegmentOptions = useMemo(
        () => payerSegments?.map(mapPayerSegment).sort(({ name: a }, { name: b }) => sortAsc(a, b)),
        [payerSegments],
    );

    // A global route keys its rule by the same id in every payer segment it is configured in
    const configuredSegments = isCreate
        ? []
        : payerSegmentsWithRoute?.map((segment) => ({
              ...segment,
              route_rule_id: globalRouteId,
          }));

    // An existing global route keeps the benefit it was created for
    const configuredSegmentRoutes = useConfiguredSegmentRoutes(
        configuredSegments,
        globalRoute?.benefit,
    );

    return (
        <Formik
            initialValues={{
                ...RouteRuleForm.initialValues(
                    globalRoute,
                    configuredSegments,
                    configuredSegmentRoutes,
                ),
                global_route: true,
                benefit: globalRoute?.benefit,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                ...RouteRuleForm.validationSchema,
                benefit: Yup.string().required(validationStrings.requiredField.errorMessage),
            })}
            onSubmit={async (values) => {
                try {
                    const routeRuleId = isCreate ? uuidv4() : globalRouteId;
                    const body = {
                        ...RouteRuleForm.submitValues(values),
                        benefit: values.benefit,
                        route_rule_id: routeRuleId,
                        // The database assigns the real version; a new route has none yet
                        route_rule_version: globalRoute?.route_rule_version ?? 0,
                        configured_payer_segments: values.payer_segments.map((segment) => ({
                            payer_id: segment.payer_id,
                            segment_id: segment.segment_id,
                            ...getSegmentPlacement(segment),
                        })),
                    };

                    await (isCreate
                        ? post(globalRoutesUrl, body)
                        : put(globalRouteUrl(globalRouteId), body));

                    await SuccessToast({ message: 'Global Route saved successfully.' });

                    goHome();
                } catch (err) {
                    handleError('Error saving global route.', err);
                }
            }}
        >
            <GlobalRouteFormContent
                isCreate={isCreate}
                isLoading={!(isCreate || configuredSegments)}
                payerSegmentOptions={payerSegmentOptions}
                configuredSegmentRoutes={configuredSegmentRoutes}
                onCancel={goHome}
            />
        </Formik>
    );
};
